// ./src/hooks/useConnectors.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";
import {
  Connector,
  ConnectorService,
  ConnectorFilter,
  ConnectorInput,
  Pagination,
} from "@/utils/api";
import { SortConfig } from "@/components/generic";
import { DEFAULT_PAGE_SIZE } from "@/config";

export interface UseConnectorsOptions {
  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<ConnectorFilter>;

  /** Initial sort configuration */
  initialSort?: SortConfig[];

  /** Auto-load data on mount */
  autoLoad?: boolean;

  /** Current user ID - data reloads when this changes */
  userId?: string | null;
}

export interface UseConnectorsReturn {
  // Data state
  connector: Connector | null;
  connectors: Connector[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<ConnectorFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  removingId: string | null;
  selectedConnector: Connector | null;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<void>;
  list: (
    filter?: Partial<ConnectorFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    shouldReset?: boolean
  ) => Promise<void>;
  getMany: (ids: string[]) => Promise<Connector[]>;
  create: (params: ConnectorInput) => Promise<Connector | null>;
  update: (id: string, params: ConnectorInput) => Promise<Connector | null>;
  remove: (id: string) => Promise<boolean>;
  removeMany: (ids: string[]) => Promise<boolean>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  // UI state management
  setSelectedConnector: (connector: Connector | null) => void;

  // Helper operations
  reset: () => void;

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortConfig[] = [{ key: "name", direction: "asc" }];

export const useConnectors = (
  options: UseConnectorsOptions = {}
): UseConnectorsReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = { status: [STATUSES.ACTIVE] },
    initialSort = DEFAULT_SORT,
    autoLoad = true,
    userId = null,
  } = options;

  // Use refs for option values to avoid dependency issues
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortConfig[]>(initialSort);
  const initialFiltersRef = useRef(initialFilters);
  const previousUserIdRef = useRef<string | null | undefined>(undefined);

  // Update refs when options change
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  useEffect(() => {
    initialFiltersRef.current = initialFilters;
  }, [initialFilters]);

  // Data state
  const [connector, setConnector] = useState<Connector | null>(null);
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters
  const [filters, setFilters] =
    useState<Partial<ConnectorFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [selectedConnector, setSelectedConnector] = useState<Connector | null>(
    null
  );

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => connectors.length === 0 && !loading,
    [connectors.length, loading]
  );

  // Helper function to update item in list if it exists, or add it if not
  const updateOrAddInList = useCallback((updatedItem: Connector) => {
    setConnectors((prev) => {
      const existingIndex = prev.findIndex(
        (item) => item.id === updatedItem.id
      );
      if (existingIndex >= 0) {
        const newItems = [...prev];
        newItems[existingIndex] = updatedItem;
        return newItems;
      } else {
        return [updatedItem, ...prev];
      }
    });
  }, []);

  // Helper function to remove item from list if it exists
  const removeFromList = useCallback((id: string) => {
    setConnectors((prev) => prev.filter((item) => item.id !== id));
  }, []);

  // Helper function to remove multiple items from list
  const removeManyFromList = useCallback((ids: string[]) => {
    const idsSet = new Set(ids);
    setConnectors((prev) => prev.filter((item) => !idsSet.has(item.id || "")));
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setConnector(null);
    setConnectors([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingId(null);
    setSelectedConnector(null);
  }, []);

  // Get single connector (matching API method name)
  const get = useCallback(
    async (id: string) => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await ConnectorService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const data = (result.data as Connector[])[0];
          setConnector(data);
          updateOrAddInList(data);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load connector";
          setLastError(errorMessage);
          setConnector(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load connector";
        setLastError(errorMessage);
        setConnector(null);
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddInList]
  );

  // List connectors (matching API method name)
  const list = useCallback(
    async (
      filter: Partial<ConnectorFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      shouldReset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !shouldReset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (shouldReset) {
          setConnectors([]);
          setCurrentPage(1);
        }
      }

      setFilters(filter);
      if (sortByParams) setSortBy(sortByParams);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => ({
          name: sort.key,
          order: sort.direction.toUpperCase() as "ASC" | "DESC",
        }));

        const result = await ConnectorService.list(
          filter as ConnectorFilter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItems = Array.isArray(result.data)
            ? (result.data as Connector[])
            : [];

          if (isAppending) {
            setConnectors((prev) => {
              const existingIds = new Set(prev.map((item) => item.id));
              const uniqueNewItems = newItems.filter(
                (item) => !existingIds.has(item.id)
              );
              return [...prev, ...uniqueNewItems];
            });
          } else {
            setConnectors(newItems);
          }

          setHasMore(newItems.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load connectors";
          setLastError(errorMessage);
          if (!isAppending) setConnectors([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load connectors";
        setLastError(errorMessage);
        if (!isAppending) setConnectors([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Get many connectors by IDs (matching API method name)
  const getMany = useCallback(
    async (ids: string[]): Promise<Connector[]> => {
      if (ids.length === 0) {
        return [];
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await ConnectorService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedItems = Array.isArray(result.data)
            ? (result.data as Connector[])
            : [];
          loadedItems.forEach((item) => {
            updateOrAddInList(item);
          });
          setLastError(null);
          return loadedItems;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load connectors";
          setLastError(errorMessage);
          return [];
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load connectors";
        setLastError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddInList]
  );

  // Create connector (matching API method name)
  const create = useCallback(
    async (params: ConnectorInput): Promise<Connector | null> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await ConnectorService.create(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItem = (result.data as Connector[])[0];
          setConnector(newItem);
          setConnectors((prev) => [newItem, ...prev]);
          setLastError(null);
          return newItem;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to create connector";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to create connector";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Update connector (matching API method name)
  const update = useCallback(
    async (id: string, params: ConnectorInput): Promise<Connector | null> => {
      if (!id) {
        setLastError("Connector ID is required for updating");
        return null;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await ConnectorService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedItem = (result.data as Connector[])[0];
          setConnector(updatedItem);
          updateOrAddInList(updatedItem);
          setLastError(null);
          return updatedItem;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to update connector";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to update connector";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddInList]
  );

  // Remove connector (matching API method name)
  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      if (!id) {
        setLastError("Connector ID is required for removal");
        return false;
      }

      setRemovingId(id);
      setLastError(null);

      try {
        const result = await ConnectorService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setConnector(null);
          removeFromList(id);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to remove connector";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to remove connector";
        setLastError(errorMessage);
        return false;
      } finally {
        setRemovingId(null);
      }
    },
    [removeFromList]
  );

  // Remove many connectors (matching API method name)
  const removeMany = useCallback(
    async (ids: string[]): Promise<boolean> => {
      if (ids.length === 0) {
        setLastError("At least one connector ID is required for removal");
        return false;
      }

      setLastError(null);

      try {
        const result = await ConnectorService.removeMany(ids);

        if (result.code === OP_RESULT_CODES.OK) {
          setConnector(null);
          removeManyFromList(ids);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to remove connectors";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to remove connectors";
        setLastError(errorMessage);
        return false;
      }
    },
    [removeManyFromList]
  );

  // Load more connectors
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(
        filters,
        { page: nextPage, pageSize: pageSizeRef.current },
        sortByRef.current,
        false
      );
    }
  }, [list, loadingMore, hasMore, currentPage, filters]);

  // Refresh connectors
  const refresh = useCallback(async () => {
    await list(
      filters,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, filters]);

  // Handle user change - reset and reload data when userId changes
  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    // Skip on initial mount (when previousUserId is undefined)
    if (previousUserId === undefined) {
      // Initial load if autoLoad is enabled and user is logged in
      if (autoLoad && userId) {
        list(
          initialFiltersRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          sortByRef.current,
          true
        );
      }
      return;
    }

    // User changed (including sign out)
    if (previousUserId !== userId) {
      // Always reset when user changes
      reset();

      // If new user is logged in and autoLoad is enabled, load their data
      if (autoLoad && userId) {
        list(
          initialFiltersRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          sortByRef.current,
          true
        );
      }
    }
  }, [userId, autoLoad, list, reset]);

  return {
    connector,
    connectors,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    currentPage,
    pageSize,
    isCreating,
    isUpdating,
    removingId,
    selectedConnector,
    get,
    list,
    getMany,
    create,
    update,
    remove,
    removeMany,
    loadMore,
    refresh,
    setSelectedConnector,
    reset,
    lastError,
    clearError,
  };
};
